import { defineStore } from 'pinia'
import { FeatureFlag, FLAG_DEFAULTS } from './types'
import { ref } from 'vue'
import { readOverrides, writeOverrides } from './utils/devOverrides'
import { readEnvFlags } from './utils/envFlags'


/*
| A flag is on when, in order: a dev override, the org's config from the
| server, the VITE_FEATURE_FLAGS build env, or FLAG_DEFAULTS says so.
*/
export const useFeatureFlagStore = defineStore('FeatureFlagStore', () => {
  const fromEnv = readEnvFlags()
  const fromServer = ref<Partial<Record<FeatureFlag, boolean>>>({})
  const overrides = ref<Partial<Record<FeatureFlag, boolean>>>(import.meta.env.DEV ? readOverrides() : {})
  const loaded = ref(false)

  function hydrate(raw: Record<string, unknown>) {
    const next: Partial<Record<FeatureFlag, boolean>> = {}
    for (const key of Object.keys(FLAG_DEFAULTS) as FeatureFlag[]) {
      if (typeof raw[key] === 'boolean') next[key] = raw[key] as boolean
    }
    fromServer.value = next
    loaded.value = true
  }

  function reset() {
    fromServer.value = {}
    loaded.value = false
  }

  function isOn(flag: FeatureFlag): boolean {
    return overrides.value[flag] ?? fromServer.value[flag] ?? fromEnv[flag] ?? FLAG_DEFAULTS[flag]
  }

  function setOverride(flag: FeatureFlag, value: boolean | undefined) {
    if (!import.meta.env.DEV) return
    const next = { ...overrides.value }
    if (value === undefined) delete next[flag]
    else next[flag] = value
    overrides.value = next
    writeOverrides(next)
  }

  function clearOverrides() {
    if (!import.meta.env.DEV) return
    overrides.value = {}
    writeOverrides({})
  }

  return { loaded, fromServer, hydrate, reset, isOn, overrides, setOverride, clearOverrides }
})